/**
 * PolygonService
 *
 * Service for polygon operations including creation, area calculation, and simplification.
 * Extracted from controllers to provide reusable polygon logic.
 */

import type maplibregl from "maplibre-gl";
import type { MapProvider } from "@/types/map_provider";
import type { Coordinate } from "./coordinate_validation_service";
import CoordinateValidationService from "./coordinate_validation_service";

export interface PolygonOptions {
  paths: Array<{lat: number; lng: number}>;
  map: maplibregl.Map;
  strokeColor?: string;
  strokeWeight?: number;
  strokeOpacity?: number;
  fillColor?: string;
  fillOpacity?: number;
}

export class PolygonService {
  /**
   * Creates a polygon on the map with the specified options
   *
   * @param mapProvider - The map provider service
   * @param options - Polygon creation options
   * @returns Promise that resolves when polygon is created
   */
  static async createPolygonOnMap(
    mapProvider: MapProvider,
    options: PolygonOptions
  ): Promise<void> {
    // Validate all path coordinates
    const validPath = options.paths.filter(point =>
      CoordinateValidationService.isValidCoordinate(point)
    );

    if (validPath.length < 3) {
      throw new Error('Polygon requires at least 3 valid points');
    }

    await mapProvider.createPolygon({
      ...options,
      paths: validPath
    });
  }

  /**
   * Fits map to polygon bounds
   *
   * @param mapProvider - The map provider service
   * @param map - The map instance
   * @param path - Array of polygon points
   * @returns Promise that resolves when map is fitted
   */
  static async fitMapToBounds(
    mapProvider: MapProvider,
    map: maplibregl.Map,
    path: Array<{lat: number; lng: number}>
  ): Promise<void> {
    const bounds = mapProvider.createBounds();

    // Extend bounds with each valid point
    for (const point of path) {
      if (CoordinateValidationService.isValidCoordinate(point)) {
        const latLng = mapProvider.createLatLng(point.lat, point.lng);
        bounds.extend(latLng);
      }
    }

    // Use LngLatBoundsLike type for fitBounds
    map.fitBounds(bounds as maplibregl.LngLatBoundsLike);
  }
}

// Default export for convenience
export default PolygonService;
